// Event-Driven State Management for EmployeeTalk System
// Backend-controlled state with component-specific data storage

export enum EAnalysisStatus {
    IDLE = 'idle',
    LOADING = 'loading',
    COMPLETED = 'completed',
    ERROR = 'error',
}

export interface IEmployeeTalkState {
    // Navigation State (Backend-controlled)
    navigation: {
        currentStep: number;
        completedSteps: number[];
        availableSteps: number[];
        availableStepsManager: number[]; // Manager sees more steps
    };

    // Component State (Each component has own state)
    components: Record<string, IComponentState>;

    // Analysis State (Read-only, Backend-controlled)
    analysis: {
        started?: boolean; // Flag indicating analysis has started
        attachmentId?: string;
        personal: IAnalysisFieldState<IPersonalData>;
        skills: IAnalysisFieldState<IExtractedSkill[]>;
        certificates: IAnalysisFieldState<IExtractedCertificate[]>;
        roles: IAnalysisFieldState<IExtractedRole[]>;
        description: IAnalysisFieldState<string>;
    };

    // Metadata
    metadata: {
        lastUpdated: string;
        version: number;
        employeeId: string;
        templateId: string;
    };
}

// Component State Interface
export interface IComponentState {
    componentType: string; // 'textInput', 'cvUpload', 'nameGender', etc.
    value: unknown; // Type-safe through Provider getComponentData<T>()
    lastModified: string;
    isValid?: boolean;
    isDirty?: boolean;
}

// Analysis Field State
export interface IAnalysisFieldState<T> {
    status: EAnalysisStatus;
    data?: T;
    confidence?: number;
    lastUpdated?: string;
    error?: string;
}

// Component Data Interfaces (Type-safe component values)
export interface ITextInputData {
    text: string;
}

export interface ICVUploadData {
    attachmentId?: string;
    fileName?: string;
    uploaded?: boolean;
    files?: IAttachment[];
    manualContinue?: boolean;
    contentError?: boolean; // True if CV content could not be extracted
}

export interface INameGenderData {
    firstName: string;
    lastName: string;
    gender?: string;
    phoneNumber?: string;
    description?: string;
    ignoredChanges?: {
        personal?: boolean;
        description?: boolean;
    };
}

export interface ISkillExtractionData {
    selectedSkills: string[];
    customSkills: string[];
}

export interface ISuggestionData {
    acceptedSuggestions: string[];
    rejectedSuggestions: string[];
    // Legacy fields for compatibility
    skills: { title: string; id?: number; level: number }[];
    certificates: { title: string; id?: number }[];
    roles: { title: string; id: number }[];
    justification: string;
    lastModified?: string;
}

export interface ICheckboxData {
    selectedOptions: string[];
}

export interface ISelectData {
    selectedValue: string | string[];
}

export interface IMultilineInputData {
    text: string;
}

export interface ISuperPowersData {
    selectedSuperPowers: string[];
}

export interface ICertificateSelectionData {
    selectedCertificates: string[];
}

// Certificate Selection Component Data (Backend-controlled, single source of truth)
// Backend sets initial state after CV analysis
// Frontend displays and sends events for selection/deselection
export interface ICertificateSelectionUIData {
    certificates: Array<{
        title: string;
        selected: boolean;
        id?: number; // Certificate ID if exists
        issuer?: string; // From CV analysis
        startDate?: string; // User input (ISO date string)
        endDate?: string; // User input (ISO date string)
    }>;
}

export interface IRoleSelectionData {
    selectedRoles: string[];
}

// Role Selection Component Data (Backend-controlled, single source of truth)
// Backend sets initial state after CV analysis
// Frontend displays and sends events for selection/deselection
export interface IRoleSelectionUIData {
    roles: Array<{
        title: string;
        selected: boolean;
        id?: number; // Role ID if exists
    }>;
}

// Skill Extraction Component Data (Backend-controlled, single source of truth)
// Backend sets initial state after CV analysis with suggested levels
// Frontend only displays and sends events for level changes or selection
export interface ISkillExtractionUIData {
    skills: Array<{
        title: string;
        level: number; // User-selected or suggested level (1-5)
        category: string;
        selected: boolean; // true = skill added to employee (check clicked)
        id?: number; // Skill ID if exists
    }>;
}

// Analysis Data Interfaces (Read-only from backend)
export interface IPersonalData {
    readonly firstName?: string;
    readonly lastName?: string;
    readonly phoneNumber?: string;
    readonly gender?: string;
    readonly extractedAt: string;
}

export interface IExtractedSkill {
    readonly id?: number; // ID if mapped to existing skill
    readonly title: string;
    readonly level?: number; // Skill level (0-5)
    readonly confidence: number;
    readonly category?: string;
    readonly context?: string;
    readonly extractedAt: string;
}

export interface IExtractedCertificate {
    readonly id?: number; // ID if mapped to existing certificate
    readonly title: string;
    readonly confidence: number;
    readonly issuer?: string;
    readonly context?: string;
    readonly extractedAt: string;
}

export interface IExtractedRole {
    readonly id?: number; // ID if mapped to existing role
    readonly title: string;
    readonly confidence: number;
    readonly context?: string;
    readonly extractedAt: string;
}

// Attachment Interface
export interface IAttachment {
    id: string;
    title: string;
    fileType: string;
    size: number;
    uploadedAt: string;
}
